// ============================================================================
// Summary Metrics Widget
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Displays summary metrics with trend indicators
// ============================================================================

import React from 'react';
import clsx from 'clsx';

interface Metric {
  label: string;
  value: string | number;
  trend?: number; // Percentage change, positive = good, negative = bad (unless specified)
  trendInverted?: boolean; // If true, negative trend is good (e.g., error rate)
  unit?: string;
  description?: string;
}

interface SummaryMetricsWidgetProps {
  title: string;
  metrics: Metric[];
  columns?: 2 | 3 | 4 | 6;
  className?: string;
}

export const SummaryMetricsWidget: React.FC<SummaryMetricsWidgetProps> = ({
  title,
  metrics,
  columns = 3,
  className,
}) => {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  };

  const getTrendColor = (trend: number | undefined, inverted: boolean = false): string => {
    if (trend === undefined || trend === 0) return 'text-[var(--color-text-secondary)]';
    const isPositive = inverted ? trend < 0 : trend > 0;
    return isPositive ? 'text-green-600' : 'text-red-600';
  };

  const formatTrend = (trend: number | undefined): string => {
    if (trend === undefined) return '';
    const sign = trend > 0 ? '+' : '';
    return `${sign}${trend.toFixed(1)}%`;
  };

  return (
    <div className={clsx('bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4', className)}>
      <h3 className="text-base font-semibold text-[var(--color-text-primary)] mb-4">{title}</h3>

      <div className={clsx('grid gap-4', gridCols[columns])}>
        {metrics.map((metric, index) => (
          <div
            key={index}
            className="bg-[var(--color-bg-subtle)] rounded-lg p-4"
          >
            <div className="text-sm text-[var(--color-text-secondary)]">{metric.label}</div>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-[var(--color-text-primary)]">
                {metric.value}
              </span>
              {metric.unit && (
                <span className="ml-1 text-sm text-[var(--color-text-secondary)]">{metric.unit}</span>
              )}
            </div>
            {metric.trend !== undefined && (
              <div className={clsx('flex items-center mt-2 text-sm', getTrendColor(metric.trend, metric.trendInverted))}>
                {metric.trend !== 0 && (
                  <svg
                    className={clsx('w-4 h-4 mr-1', metric.trend < 0 && 'transform rotate-180')}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                )}
                <span>{formatTrend(metric.trend)}</span>
                <span className="ml-1 text-[var(--color-text-secondary)]">vs last period</span>
              </div>
            )}
            {metric.description && (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{metric.description}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SummaryMetricsWidget;
